import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    ticketsListWrapper: {
        position: "relative",
        display: "flex",
        height: "100%",
        flexDirection: "column",
        overflow: "hidden",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    },

    ticketsList: {
        flex: 1,
        maxHeight: "100%",
        overflowY: "scroll",
        ...theme.scrollbarStyles,
        borderTop: "2px solid rgba(0, 0, 0, 0.12)",
    },

    ticketsListHeader: {
        color: "rgb(67, 83, 105)",
        zIndex: 2,
        backgroundColor: "white",
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },

    ticketsCount: {
        fontWeight: "normal",
        color: "rgb(104, 121, 146)",
        marginLeft: "8px",
        fontSize: "14px",
    },

    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: "14px",
        lineHeight: "1.4",
    },

    noTicketsTitle: {
        textAlign: "center",
        fontSize: "16px",
        fontWeight: "600",
        margin: "0px",
    },

    noTicketsDiv: {
        display: "flex",
        // height: "190px",
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
}));

// ✅ Função de ordenação que prioriza tickets com mensagens não lidas
const ticketSortAsc = (a, b) => {
    // Tickets com mensagens não lidas sempre ficam no topo
    if (a.unreadMessages > 0 && b.unreadMessages === 0) return -1;
    if (a.unreadMessages === 0 && b.unreadMessages > 0) return 1;
    
    // Se ambos têm ou não têm mensagens não lidas, ordenar por updatedAt
    if (a.updatedAt < b.updatedAt) {
        return -1;
    }
    if (a.updatedAt > b.updatedAt) {
        return 1;
    }
    return 0;
}

const ticketSortDesc = (a, b) => {
    // Tickets com mensagens não lidas sempre ficam no topo
    if (a.unreadMessages > 0 && b.unreadMessages === 0) return -1;
    if (a.unreadMessages === 0 && b.unreadMessages > 0) return 1;
    
    // Se ambos têm ou não têm mensagens não lidas, ordenar por updatedAt
    if (a.updatedAt > b.updatedAt) {
        return -1;
    }
    if (a.updatedAt < b.updatedAt) {
        return 1;
    }
    return 0;
}

// Merge incoming ticket with existing, preserving tags quando o payload não traz (evita tag sumir ao receber mensagem / no servidor)
const mergeTicketPreservingTags = (existing, incoming) => {
    const merged = { ...existing, ...incoming };
    if (incoming.contact && existing.contact) {
        merged.contact = { ...existing.contact, ...incoming.contact };
        const hasIncomingContactTags = Array.isArray(incoming.contact?.tags) && incoming.contact.tags.length > 0;
        if (!hasIncomingContactTags && Array.isArray(existing.contact?.tags) && existing.contact.tags.length > 0) {
            merged.contact.tags = existing.contact.tags;
        }
    } else if (existing.contact && !incoming.contact) {
        merged.contact = existing.contact;
    }
    const hasIncomingTicketTags = Array.isArray(incoming.tags) && incoming.tags.length > 0;
    if (!hasIncomingTicketTags && Array.isArray(existing.tags) && existing.tags.length > 0) {
        merged.tags = existing.tags;
    }
    return merged;
};

const reducer = (state, action) => {
    //console.log("action", action, state)
    const sortDir = action.sortDir;
    
    if (action.type === "LOAD_TICKETS") {
        const newTickets = action.payload;

        newTickets.forEach((ticket) => {
            const ticketIndex = state.findIndex((t) => t.id === ticket.id);
            if (ticketIndex !== -1) {
                state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
            } else {
                state.push(ticket);
            }
        });
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET_UNREAD") {
        const ticketId = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state[ticketIndex].unreadMessages = 0;
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            state[ticketIndex] = mergeTicketPreservingTags(state[ticketIndex], ticket);
        } else {
            // ✅ Se tem mensagens não lidas, adicionar no topo, senão adicionar no final
            if (ticket.unreadMessages > 0) {
                state.unshift(ticket);
            } else {
                state.push(ticket);
            }
        }
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
        const ticket = action.payload;

        const ticketIndex = state.findIndex((t) => t.id === ticket.id);
        if (ticketIndex !== -1) {
            const merged = mergeTicketPreservingTags(state[ticketIndex], ticket);
            state[ticketIndex] = merged;
            // ✅ Se tem mensagens não lidas, mover para o topo
            if (ticket.unreadMessages > 0) {
                state.unshift(state.splice(ticketIndex, 1)[0]);
            }
        } else {
            if (action.status === action.payload.status) {
                // ✅ Se tem mensagens não lidas, adicionar no topo
                if (ticket.unreadMessages > 0) {
                    state.unshift(ticket);
                } else {
                    state.push(ticket);
                }
            }
        }
        // ✅ Sempre aplicar ordenação para manter tickets com unreadMessages no topo
        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "UPDATE_TICKET_CONTACT") {
        const contact = action.payload;
        const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
        if (ticketIndex !== -1) {
            const existing = state[ticketIndex];
            const existingContact = existing.contact;
            const mergedContact = existingContact
                ? { ...existingContact, ...contact }
                : contact;
            const hasIncomingTags = Array.isArray(contact?.tags) && contact.tags.length > 0;
            if (!hasIncomingTags && Array.isArray(existingContact?.tags) && existingContact.tags.length > 0) {
                mergedContact.tags = existingContact.tags;
            }
            state[ticketIndex] = { ...existing, contact: mergedContact };
        }
        return [...state];
    }

    if (action.type === "DELETE_TICKET") {
        const ticketId = action.payload;
        const ticketIndex = state.findIndex((t) => t.id === ticketId);
        if (ticketIndex !== -1) {
            state.splice(ticketIndex, 1);
        }

        if (sortDir && ['ASC', 'DESC'].includes(sortDir)) {
            sortDir === 'ASC' ? state.sort(ticketSortAsc) : state.sort(ticketSortDesc);
        }

        return [...state];
    }

    if (action.type === "RESET") {
        return [];
    }
};

const TicketsListCustom = (props) => {
    const {
        setTabOpen,
        status,
        searchParam,
        searchOnMessages,
        tags,
        users,
        showAll,
        selectedQueueIds,
        updateCount,
        updateUnreadCount,
        updateTotalCount,
        style,
        whatsappIds,
        forceSearch,
        statusFilter,
        userFilter,
        sortTickets,
        refreshTrigger,
        onTicketStatusChange
    } = props;

    const classes = useStyles();
    const [pageNumber, setPageNumber] = useState(1);
    let [ticketsList, dispatch] = useReducer(reducer, []);
    //   const socketManager = useContext(SocketContext);
    const { user, socket } = useContext(AuthContext);

    const { profile, queues } = user;
    const showTicketWithoutQueue = user.allTicket === 'enable';
    const companyId = user.companyId;

    useEffect(() => {
        dispatch({ type: "RESET" });
        setPageNumber(1);
    }, [status, searchParam, dispatch, showAll, tags, users, forceSearch, selectedQueueIds, whatsappIds, statusFilter, sortTickets, searchOnMessages]);

    const { tickets, hasMore, loading, count: totalCount } = useTickets({
        pageNumber,
        searchParam,
        status,
        showAll,
        searchOnMessages: searchOnMessages ? "true" : "false",
        tags: JSON.stringify(tags),
        users: JSON.stringify(users),
        queueIds: JSON.stringify(selectedQueueIds),
        whatsappIds: JSON.stringify(whatsappIds),
        statusFilter: JSON.stringify(statusFilter),
        userFilter,
        sortTickets,
        refreshTrigger
    });


    useEffect(() => {
        // const queueIds = queues.map((q) => q.id);
        // const filteredTickets = tickets.filter(
        //     (t) => queueIds.indexOf(t.queueId) > -1
        // );
        // const allticket = user.allTicket === 'enabled';
        // if (profile === "admin" || allTicket || allowGroup || allHistoric) {
        if (companyId) {
            dispatch({
                type: "LOAD_TICKETS",
                payload: tickets,
                status,
                sortDir: sortTickets
            });
        }
        // } else {
        //  dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
        // }

    }, [tickets]);

    useEffect(() => {
        const shouldUpdateTicket = ticket => {
            return (!ticket?.userId || ticket?.userId === user?.id || showAll) &&
                ((!ticket?.queueId && showTicketWithoutQueue) || selectedQueueIds.indexOf(ticket?.queueId) > -1)
            // (!blockNonDefaultConnections || (ticket.status == 'group' && ignoreUserConnectionForGroups) || !user?.whatsappId || ticket.whatsappId == user?.whatsappId);
        }
        // const shouldUpdateTicketUser = (ticket) =>
        //     selectedQueueIds.indexOf(ticket?.queueId) > -1 && (ticket?.userId === user?.id || !ticket?.userId);

        const notBelongsToUserQueues = (ticket) =>
            ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

        const belongsToFlowList = (ticket) =>
            ticket && ticket.flowWebhook === true && ticket.flowTriggeredManually === true && (ticket.status === "open" || ticket.status === "pending");

        const onCompanyTicketTicketsList = (data) => {
            // console.log("onCompanyTicketTicketsList", data)
            if (data.action === "updateUnread") {
                dispatch({
                    type: "RESET_UNREAD",
                    payload: data.ticketId,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // Aba Flow: ticket tem status "open" no backend; considerar por flowWebhook
            const updateMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : (data.ticket && data.ticket.status === status);
            if (data.action === "update" &&
                shouldUpdateTicket(data.ticket) && updateMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
                // Ticket entrou ou foi atualizado nesta aba (ex.: devolvido para Aguardando) -> atualizar contador
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // Aba Flow: quando fluxo termina (flowWebhook false), remover da lista
            if (status === "flow" && data.action === "update" && data.ticket &&
                shouldUpdateTicket(data.ticket) && data.ticket.status === "open" && !data.ticket.flowWebhook) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            if (data.action === "update" && notBelongsToUserQueues(data.ticket)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }

            if (data.action === "delete") {
                dispatch({
                    type: "DELETE_TICKET", payload: data?.ticketId, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // create: novo ticket entrou na aba -> atualizar contadores
            if (data.action === "create" && data.ticket && shouldUpdateTicket(data.ticket) &&
                (status === "flow" ? belongsToFlowList(data.ticket) : data.ticket.status === status)) {
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
            // update: ticket saiu desta aba (ex.: pending -> open) -> remover da lista e atualizar contadores
            if (data.action === "update" && data.ticket && shouldUpdateTicket(data.ticket) && !updateMatchesStatus &&
                (status === "flow" ? !belongsToFlowList(data.ticket) : data.ticket.status !== status)) {
                dispatch({
                    type: "DELETE_TICKET", payload: data.ticket?.id, status: status,
                    sortDir: sortTickets
                });
                if (typeof onTicketStatusChange === "function") onTicketStatusChange();
            }
        };

        const onCompanyAppMessageTicketsList = (data) => {
            const messageMatchesStatus = status === "flow"
                ? belongsToFlowList(data.ticket)
                : (data.ticket && data.ticket.status === status);
            if (data.action === "create" &&
                shouldUpdateTicket(data.ticket) && messageMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET_UNREAD_MESSAGES",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
            // ✅ CORREÇÃO: Atualizar ticket quando mensagem é atualizada (ex: deletada)
            if (data.action === "update" && data.ticket &&
                shouldUpdateTicket(data.ticket) && messageMatchesStatus) {
                dispatch({
                    type: "UPDATE_TICKET",
                    payload: data.ticket,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onCompanyContactTicketsList = (data) => {
            if (data.action === "update" && data.contact) {
                dispatch({
                    type: "UPDATE_TICKET_CONTACT",
                    payload: data.contact,
                    status: status,
                    sortDir: sortTickets
                });
            }
        };

        const onConnectTicketsList = () => {
            if (status) {
                socket.emit("joinTickets", status);
            } else {
                socket.emit("joinNotification");
            }
        }

        socket.on("connect", onConnectTicketsList)
        socket.on(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
        socket.on(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
        socket.on(`company-${companyId}-contact`, onCompanyContactTicketsList);

        return () => {
            if (status) {
                socket.emit("leaveTickets", status);
            } else {
                socket.emit("leaveNotification");
            }
            socket.off("connect", onConnectTicketsList);
            socket.off(`company-${companyId}-ticket`, onCompanyTicketTicketsList);
            socket.off(`company-${companyId}-appMessage`, onCompanyAppMessageTicketsList);
            socket.off(`company-${companyId}-contact`, onCompanyContactTicketsList);
        };

    }, [status, showAll, user, selectedQueueIds, tags, users, profile, queues, sortTickets, showTicketWithoutQueue, onTicketStatusChange]);

    useEffect(() => {
        if (typeof updateCount === "function") {
            updateCount(ticketsList.length);
        }
        if (typeof updateUnreadCount === "function") {
            const unreadTickets = ticketsList.filter(t => Number(t.unreadMessages) > 0).length;
            updateUnreadCount(unreadTickets);
        }
        // Sincronizar contador total: quando a lista diminui (ex.: ticket fechado/ignorado), usar ticketsList.length para o contador atualizar na hora; quando a lista cresce (socket), usar o maior
        if (typeof updateTotalCount === "function") {
            const total = totalCount !== undefined && totalCount !== null
                ? (ticketsList.length < totalCount ? ticketsList.length : Math.max(ticketsList.length, totalCount))
                : ticketsList.length;
            updateTotalCount(total);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketsList, totalCount]);

    const loadMore = () => {
        setPageNumber((prevState) => prevState + 1);
    };

    const handleScroll = (e) => {
        if (!hasMore || loading) return;

        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollHeight - (scrollTop + 100) < clientHeight) {
            loadMore();
        }
    };

    if (status && status !== "search") {
        if (status === "flow") {
            // Aba Flow: apenas fluxo disparado manualmente (não integração automática)
            ticketsList = ticketsList.filter(ticket => ticket.flowWebhook === true && ticket.flowTriggeredManually === true && (ticket.status === "open" || ticket.status === "pending"));
        } else if (status === "open") {
            // Na aba Open não mostrar tickets em fluxo manual (esses só na aba Flow)
            ticketsList = ticketsList.filter(ticket => ticket.status === "open" && !(ticket.flowWebhook === true && ticket.flowTriggeredManually === true));
        } else {
            ticketsList = ticketsList.filter(ticket => ticket.status === status);
        }
    }

    return (
        <Paper className={classes.ticketsListWrapper} style={style}>
            <Paper
                square
                name="closed"
                elevation={0}
                className={classes.ticketsList}
                onScroll={handleScroll}
            >
                <List style={{ paddingTop: 0 }} >
                    {ticketsList.length === 0 && !loading ? (
                        <div className={classes.noTicketsDiv}>
                            <span className={classes.noTicketsTitle}>
                                {i18n.t("ticketsList.noTicketsTitle")}
                            </span>
                            <p className={classes.noTicketsText}>
                                {i18n.t("ticketsList.noTicketsMessage")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {ticketsList.map((ticket) => (
                                // <List key={ticket.id}>
                                //     {console.log(ticket)}
                                <TicketListItem
                                    ticket={ticket}
                                    key={ticket.id}
                                    setTabOpen={setTabOpen}
                                />
                                // </List>
                            ))}
                        </>
                    )}
                    {loading && <TicketsListSkeleton />}
                </List>
            </Paper>
        </Paper>
    );
};

export default TicketsListCustom;
